import { Controller } from '@hotwired/stimulus'
import { loadGoogleMaps } from '~/javascript/utils/google_maps'

// Interactive Map Controller
// Upgrades static map images to interactive Google Maps on click/tap
// Uses click-to-load pattern to minimize initial page load and API usage
export default class extends Controller {
  static targets = ['staticMap', 'interactiveContainer', 'loadButton']

  static values = {
    apiKey: String,
    latitude: Number,
    longitude: Number,
    zoom: { type: Number, default: 15 },
    mapStyle: { type: String, default: '' },
    markerTitle: String,
    address: String,
    loaded: { type: Boolean, default: false }
  }

  connect() {
    // Bind handler once and store reference for proper cleanup
    this.boundHandleClick = this.handleClick.bind(this)

    // Add click handler to static map for upgrade
    if (this.hasStaticMapTarget) {
      this.staticMapTarget.addEventListener('click', this.boundHandleClick)
      this.staticMapTarget.style.cursor = 'pointer'
    }
  }

  disconnect() {
    // Remove event listener using stored reference
    if (this.hasStaticMapTarget && this.boundHandleClick) {
      this.staticMapTarget.removeEventListener('click', this.boundHandleClick)
    }

    // Clean up map instance
    if (this.map) {
      this.map = null
    }
  }

  handleClick(event) {
    event.preventDefault()
    if (!this.loadedValue) {
      this.loadInteractiveMap()
    }
  }

  // Called when user clicks "View Interactive Map" button
  loadMap() {
    this.loadInteractiveMap()
  }

  async loadInteractiveMap() {
    if (this.loadedValue || this.loading) return

    this.loading = true
    this.showLoadingState()

    try {
      // Validate required values
      if (!this.apiKeyValue) {
        console.warn('Interactive map: No API key provided')
        this.openInGoogleMaps()
        return
      }

      if (!this.hasValidCoordinates()) {
        console.warn('Interactive map: No valid coordinates')
        this.openInGoogleMaps()
        return
      }

      // Load Google Maps API
      const google = await loadGoogleMaps(this.apiKeyValue)

      // Create interactive map container if it doesn't exist
      this.ensureInteractiveContainer()

      // Initialize the map
      const mapOptions = {
        center: { lat: this.latitudeValue, lng: this.longitudeValue },
        zoom: this.zoomValue,
        mapTypeControl: false,
        streetViewControl: true,
        fullscreenControl: true,
        zoomControl: true
      }

      // Apply custom map styles if provided
      if (this.mapStyleValue) {
        try {
          mapOptions.styles = JSON.parse(this.mapStyleValue)
        } catch (e) {
          console.warn('Invalid map style JSON:', e)
        }
      }

      this.map = new google.maps.Map(this.interactiveContainerTarget, mapOptions)

      // Add marker
      new google.maps.Marker({
        position: { lat: this.latitudeValue, lng: this.longitudeValue },
        map: this.map,
        title: this.markerTitleValue || 'Location',
        animation: google.maps.Animation.DROP
      })

      // Show interactive map, hide static
      this.showInteractiveMap()
      this.loadedValue = true
    } catch (error) {
      console.error('Failed to load interactive map:', error)
      this.hideLoadingState()
      // Fall back to opening Google Maps
      this.openInGoogleMaps()
    } finally {
      this.loading = false
    }
  }

  hasValidCoordinates() {
    // Note: 0 is a valid coordinate (e.g., 0,0 is in the Atlantic Ocean)
    // Use explicit null/undefined checks instead of truthy checks
    return typeof this.latitudeValue === 'number' &&
      typeof this.longitudeValue === 'number' &&
      !isNaN(this.latitudeValue) && !isNaN(this.longitudeValue)
  }

  ensureInteractiveContainer() {
    if (!this.hasInteractiveContainerTarget) {
      const container = document.createElement('div')
      container.setAttribute('data-public--interactive-map-target', 'interactiveContainer')
      container.className = 'w-full h-full absolute inset-0 hidden'
      this.element.appendChild(container)
    }
  }

  showLoadingState() {
    if (this.hasStaticMapTarget) {
      this.staticMapTarget.classList.add('opacity-50')
    }
    if (this.hasLoadButtonTarget) {
      this.loadButtonTarget.disabled = true
      this.loadButtonTarget.innerHTML = `
        <svg class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Loading...
      `
    }
  }

  hideLoadingState() {
    if (this.hasStaticMapTarget) {
      this.staticMapTarget.classList.remove('opacity-50')
    }
    if (this.hasLoadButtonTarget) {
      this.loadButtonTarget.disabled = false
      this.loadButtonTarget.textContent = 'View Interactive Map'
    }
  }

  showInteractiveMap() {
    if (this.hasStaticMapTarget) {
      this.staticMapTarget.classList.add('hidden')
    }
    if (this.hasLoadButtonTarget) {
      this.loadButtonTarget.classList.add('hidden')
    }
    if (this.hasInteractiveContainerTarget) {
      this.interactiveContainerTarget.classList.remove('hidden')
    }
  }

  openInGoogleMaps() {
    let url
    if (this.hasValidCoordinates()) {
      url = `https://www.google.com/maps?q=${this.latitudeValue},${this.longitudeValue}`
    } else if (this.addressValue) {
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(this.addressValue)}`
    } else {
      return
    }
    window.open(url, '_blank', 'noopener')
  }

  // Action for getting directions
  getDirections() {
    let destination
    if (this.hasValidCoordinates()) {
      destination = `${this.latitudeValue},${this.longitudeValue}`
    } else if (this.addressValue) {
      destination = this.addressValue
    } else {
      return
    }
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`
    window.open(url, '_blank', 'noopener')
  }
}
